<template>
  <q-card v-if="pricingStrategy.count_prices" flat style="max-width: 550px">
    <q-card-section>
      <div class="text-h6">Pricing Strategy</div>
      <div class="text-grey-7">
        Review whether the property is utilizing pricing strategies and how frequently prices are
        being updated. This is based on
        <b>{{ formatNumber(pricingStrategy.count_prices) }}</b>
        observed price changes for the property over the past 6 months.
      </div>
    </q-card-section>
    <q-separator class="q-mb-md" />
    <q-list>
      <q-item v-if="pricingStrategy.is_using_rev_management !== null">
        <q-item-section>
          <q-item-label>Using Revenue Management</q-item-label>
          <q-item-label caption lines="2"
            >Estimated based on how frequently prices have changed
          </q-item-label>
        </q-item-section>
        <q-item-section side>
          {{ pricingStrategy.is_using_rev_management ? 'Yes' : 'No' }}
        </q-item-section>
      </q-item>
      <q-separator v-if="pricingStrategy.avg_price_change !== null" spaced inset />
      <q-item v-if="pricingStrategy.avg_price_change !== null">
        <q-item-section>
          <q-item-label>Average Price Change</q-item-label>
          <q-item-label caption lines="2"
            >Average price difference between the first and last price
          </q-item-label>
        </q-item-section>
        <q-item-section side>
          {{ formatPercent(pricingStrategy.avg_price_change) }}
        </q-item-section>
      </q-item>
      <q-separator v-if="pricingStrategy.avg_duration !== null" spaced inset />
      <q-item v-if="pricingStrategy.avg_duration !== null">
        <q-item-section>
          <q-item-label>Average Price Duration</q-item-label>
          <q-item-label caption lines="2"
            >Average number of days a price is active before changing
          </q-item-label>
        </q-item-section>
        <q-item-section side>{{ formatNumber(pricingStrategy.avg_duration) }} days </q-item-section>
      </q-item>
      <q-separator v-if="pricingStrategy.avg_time_on_market !== null" spaced inset />
      <q-item v-if="pricingStrategy.avg_time_on_market !== null" class="q-mb-sm">
        <q-item-section>
          <q-item-label>Average Time on Market</q-item-label>
          <q-item-label caption lines="2"
            >Average number of days a unit is on the market before leasing
          </q-item-label>
        </q-item-section>
        <q-item-section side
          >{{ formatNumber(pricingStrategy.avg_time_on_market) }} days</q-item-section
        >
      </q-item>
      <q-separator spaced inset />
      <q-item>
        <q-item-section>
          <q-item-label>Day of Week Distribution</q-item-label>
          <q-item-label lines="2" caption class="q-mb-sm">
            Number of times a price change occurred on each day of the week
          </q-item-label>
          <div class="row q-gutter-x-xs justify-between q-mb-sm">
            <div
              v-for="(x, idx) in pricingStrategy.day_of_week_distribution"
              :key="idx"
              :style="{
                'background-color': x / pricingStrategy.count_prices > 0.1 ? '#536DFE' : 'white',
                color: x / pricingStrategy.count_prices > 0.1 ? 'white' : '#4438ca'
              }"
              class="col text-center q-pa-xs"
              style="
                font-size: 10px;
                border-radius: 4px;
                max-height: 26px;
                text-wrap: none;
                white-space: nowrap;
              "
            >
              {{ daysOfWeek[idx] }}: {{ ((x / pricingStrategy.count_prices) * 100).toFixed(0) }}%
            </div>
          </div>
        </q-item-section>
      </q-item>
    </q-list>
  </q-card>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import type { PricingStrategy } from 'common'
import { formatNumber, formatPercent } from '@/utils'

export default {
  name: 'CardPricingStrategy',
  props: {
    pricingStrategy: {
      type: Object as PropType<PricingStrategy>,
      required: true
    }
  },
  data() {
    return {
      daysOfWeek: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
      formatPercent,
      formatNumber
    }
  }
}
</script>

<style scoped>
/* Your component styles here */
</style>
